'use client'

import { useAtom } from 'jotai'
import { useCallback, useMemo } from 'react'

import { getExchangeLabel } from '@/lib/configs/exchange-registry'
import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { isOrderEligibleAccount } from '@/lib/utils/account-select'
import { useUserAccounts } from '@/service/account.hook'
import { defaultOrderAccountByTradeVenueAtom } from '@/state/order'
import type { PublicAccount } from '@/types/generated/common/account'
import type { TradeVenue } from '@/types/generated/common/types'

export interface DefaultOrderAccountVenue {
  tradeVenue: TradeVenue
  label: string
  /** The venue's accounts that can submit orders. */
  accounts: PublicAccount[]
  /** The account marked as default, when it is still one of `accounts`; else Auto. */
  defaultAccountId: string | undefined
}

/**
 * The default account for new Order Form rows, per venue (defaultOrderAccountByTradeVenueAtom),
 * for the pickers in Order Form settings, General settings and the Cmd+K palette. Unset is
 * "Auto": resolveAccount falls through to the remembered account, then the venue default.
 */
export function useDefaultOrderAccounts() {
  const { data } = useUserAccounts()
  const [defaultByTradeVenue, setDefaultByTradeVenue] = useAtom(defaultOrderAccountByTradeVenueAtom)

  const venues = useMemo<DefaultOrderAccountVenue[]>(() => {
    const byVenue = new Map<TradeVenue, PublicAccount[]>()
    for (const account of (data?.accounts ?? []).filter(isOrderEligibleAccount)) {
      byVenue.set(account.tradeVenue, [...(byVenue.get(account.tradeVenue) ?? []), account])
    }
    return [...byVenue.entries()].map(([tradeVenue, accounts]) => {
      const saved = defaultByTradeVenue[tradeVenue]
      return {
        tradeVenue,
        label: getExchangeLabel(toExchangeEnum(tradeVenueToExchangeKey(tradeVenue))),
        accounts,
        defaultAccountId: accounts.some((a) => a.accountId === saved) ? saved : undefined,
      }
    })
  }, [data?.accounts, defaultByTradeVenue])

  /** `undefined` clears the venue's default (Auto). */
  const setDefaultAccount = useCallback(
    (tradeVenue: TradeVenue, accountId: string | undefined) =>
      setDefaultByTradeVenue((current) => {
        const next = { ...current }
        if (accountId) next[tradeVenue] = accountId
        else delete next[tradeVenue]
        return next
      }),
    [setDefaultByTradeVenue]
  )

  return { venues, setDefaultAccount }
}
